import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Check, Sparkles, Crown, Building2, User2, Bell, Zap, Shield, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FEATURES = {
  starter: ["Jusqu'à 5 offres actives", "Recherche candidats limitée (50/mois)", "Messagerie standard", "Support email"],
  pro: ["Offres illimitées", "Recherche candidats illimitée", "Mise en avant des offres", "Analytics avancés", "Support prioritaire"],
  enterprise: ["Tout du plan Pro", "API d'intégration", "Account Manager dédié", "SLA 99.9%", "Onboarding personnalisé"],
  premium: [
    "Alertes emploi en temps réel par email",
    "Badge Premium sur votre profil",
    "Boost de visibilité dans la recherche employeur",
    "Mot-clé personnalisé pour vos alertes",
    "Coach IA illimité (CV & lettres)",
  ],
  free: ["Profil + CV PDF", "Postuler à toutes les offres", "Récap d'alertes quotidien", "Coach IA (3/mois)", "Messagerie standard"],
};

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [audienceTab, setAudienceTab] = useState(user?.role === "candidate" ? "candidate" : "employer");
  const [interval, setInterval] = useState("month"); // 'month' or 'year'

  useEffect(() => { api.get("/payments/plans").then((r) => setPlans(r.data)); }, []);
  useEffect(() => { if (user?.role) setAudienceTab(user.role === "candidate" ? "candidate" : "employer"); }, [user]);

  const checkout = async (planId) => {
    if (!user) { nav("/login"); return; }
    setLoading(planId);
    try {
      const origin = window.location.origin;
      const r = await api.post("/payments/checkout", { plan_id: planId, origin_url: origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
      setLoading(null);
    }
  };

  const findPlan = (tier, audience) => plans.find((p) => p.tier === tier && p.audience === audience && p.interval === interval);
  const findPlanMonth = (tier, audience) => plans.find((p) => p.tier === tier && p.audience === audience && p.interval === "month");

  const employerTiers = ["starter", "pro", "enterprise"];

  const yearSavingPercent = (tier, audience) => {
    const m = findPlanMonth(tier, audience);
    const y = plans.find((p) => p.tier === tier && p.audience === audience && p.interval === "year");
    if (!m || !y) return 0;
    const totalMonthly = m.amount * 12;
    return Math.round(((totalMonthly - y.amount) / totalMonthly) * 100);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="pricing-page">
      <div className="text-center mb-8">
        <div className="mec-overline mb-3">{t("pricing.overline")}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 mb-4">{t("pricing.title")}</h1>
        <p className="text-slate-600 max-w-xl mx-auto">{t("pricing.subtitle")}</p>
      </div>

      {/* RGPD / UE compliance trust banner */}
      <div className="max-w-3xl mx-auto mb-8" data-testid="trust-banner">
        <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 via-white to-emerald-50 border border-slate-200">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#004EEB] flex items-center justify-center text-white">
            <Shield className="w-5 h-5" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="font-display font-semibold text-slate-900 text-sm">
              {t("pricing.trust_title")}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {t("pricing.trust_subtitle")}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[11px] text-slate-600">
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> RGPD</span>
            <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Stripe</span>
            <span className="flex items-center gap-1"><X className="w-3 h-3 text-slate-400" /> {t("pricing.trust_no_commitment")}</span>
          </div>
        </div>
      </div>

      {/* Monthly/Yearly toggle */}
      <div className="flex justify-center mb-8" data-testid="interval-toggle">
        <div className="inline-flex items-center bg-slate-100 p-1 rounded-full">
          <button
            onClick={() => setInterval("month")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${interval === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            data-testid="toggle-monthly"
          >
            Mensuel
          </button>
          <button
            onClick={() => setInterval("year")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition flex items-center gap-1.5 ${interval === "year" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            data-testid="toggle-yearly"
          >
            Annuel
            <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white text-[9px] h-4 px-1.5">−17%</Badge>
          </button>
        </div>
      </div>

      <Tabs value={audienceTab} onValueChange={setAudienceTab} className="w-full">
        <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-10">
          <TabsTrigger value="candidate" data-testid="tab-pricing-candidate"><User2 className="w-4 h-4 mr-1.5" /> {t("pricing.tab_candidates")}</TabsTrigger>
          <TabsTrigger value="employer" data-testid="tab-pricing-employer"><Building2 className="w-4 h-4 mr-1.5" /> {t("pricing.tab_employers")}</TabsTrigger>
        </TabsList>

        <TabsContent value="candidate">
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {/* Free */}
            <div className="mec-card p-7" data-testid="plan-free-candidate">
              <Bell className="w-6 h-6 text-slate-400 mb-2" />
              <div className="font-display text-xl font-bold text-slate-900 mb-1">{t("pricing.free")}</div>
              <div className="text-sm text-slate-500 mb-5">{t("pricing.free_sub")}</div>
              <div className="mb-5">
                <span className="font-display text-4xl font-bold text-slate-900">0€</span>
                <span className="text-slate-500 text-sm">{t("pricing.free_forever")}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {FEATURES.free.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button variant="outline" className="w-full" disabled>{t("pricing.current_plan")}</Button>
            </div>

            {/* Premium */}
            {(() => {
              const plan = findPlan("premium", "candidate");
              if (!plan) return null;
              const saving = interval === "year" ? yearSavingPercent("premium", "candidate") : 0;
              return (
                <div key={plan.id} className="mec-card p-7 border-amber-300 border-2 relative bg-gradient-to-br from-amber-50/40 to-white" data-testid={`plan-${plan.id}`}>
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white hover:bg-amber-500">
                    <Crown className="w-3 h-3 mr-1" /> {t("pricing.premium_label")}
                  </Badge>
                  <Zap className="w-6 h-6 text-amber-500 mb-2" />
                  <div className="font-display text-xl font-bold text-slate-900 mb-1">{plan.name}</div>
                  <div className="text-sm text-slate-500 mb-5">{t("pricing.candidate_premium_sub")}</div>
                  <div className="mb-5">
                    <span className="font-display text-4xl font-bold text-slate-900">{plan.amount.toFixed(2).replace(".", ",")}€</span>
                    <span className="text-slate-500 text-sm"> /{interval === "month" ? "mois" : "an"}</span>
                    {interval === "year" && saving > 0 && <div className="text-xs text-emerald-700 font-semibold mt-1">Économisez {saving}%</div>}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {FEATURES.premium.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => checkout(plan.id)} disabled={loading === plan.id} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid={`subscribe-${plan.id}`}>
                    {loading === plan.id ? t("pricing.redirecting") : t("pricing.go_premium_btn")}
                  </Button>
                </div>
              );
            })()}
          </div>
        </TabsContent>

        <TabsContent value="employer">
          <div className="grid md:grid-cols-3 gap-5">
            {employerTiers.map((tier) => {
              const plan = findPlan(tier, "employer");
              if (!plan) return null;
              const isPro = tier === "pro";
              const saving = interval === "year" ? yearSavingPercent(tier, "employer") : 0;
              const featuresList = FEATURES[tier] || [];
              return (
                <div key={plan.id} className={`mec-card p-7 ${isPro ? "border-[#004EEB] border-2 relative" : ""}`} data-testid={`plan-${plan.id}`}>
                  {isPro && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#004EEB] text-white hover:bg-[#003BCC]">
                      <Sparkles className="w-3 h-3 mr-1" /> {t("pricing.recommended")}
                    </Badge>
                  )}
                  {tier === "enterprise" && <Crown className="w-6 h-6 text-amber-500 mb-2" />}
                  <div className="font-display text-xl font-bold text-slate-900 mb-1">{plan.name}</div>
                  <div className="text-sm text-slate-500 mb-5">
                    {tier === "starter" ? t("pricing.ideal_starter") : tier === "pro" ? t("pricing.ideal_pro") : t("pricing.ideal_enterprise")}
                  </div>
                  <div className="mb-5">
                    <span className="font-display text-4xl font-bold text-slate-900">{Math.round(plan.amount)}€</span>
                    <span className="text-slate-500 text-sm"> /{interval === "month" ? "mois" : "an"}</span>
                    {interval === "year" && saving > 0 && <div className="text-xs text-emerald-700 font-semibold mt-1">Économisez {saving}%</div>}
                  </div>
                  <ul className="space-y-2 mb-6">
                    {featuresList.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => checkout(plan.id)} disabled={loading === plan.id} className={`w-full ${isPro ? "bg-[#004EEB] hover:bg-[#003BCC] text-white" : ""}`} variant={isPro ? "default" : "outline"} data-testid={`subscribe-${plan.id}`}>
                    {loading === plan.id ? t("pricing.redirecting") : `${t("pricing.choose_plan")} ${plan.name}`}
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-12 text-center text-sm text-slate-500">{t("pricing.secure_payment")}</div>
    </div>
  );
}
